<template>
    <div class="gs" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.enter.prevent="choose(flat[active])"
         @keydown.esc="close">
        <label class="gs-box" :class="{focus: open}">
            <i class="el-icon-search" aria-hidden="true"></i>
            <input ref="input" v-model="q" type="search" autocomplete="off" :placeholder="placeholder"
                   aria-label="Search the Portal" role="combobox" aria-autocomplete="list" :aria-expanded="String(open && !!q)"
                   aria-controls="gs-results" @focus="open = true" @blur="onBlur">
            <kbd v-if="!q">{{ isMac ? '⌘K' : 'Ctrl K' }}</kbd>
        </label>
        <div v-if="open && q.trim().length >= 2" id="gs-results" class="gs-panel" role="listbox" @mousedown.prevent>
            <div v-if="loading && !groups.length" class="gs-empty">Searching…</div>
            <div v-else-if="!groups.length" class="gs-empty">Nothing found for “{{ q.trim() }}”.</div>
            <div v-for="g in groups" :key="g.kind" class="gs-group">
                <div class="gs-label">{{ g.label }}</div>
                <button v-for="item in g.items" :key="g.kind + item.id" type="button" role="option" class="gs-item"
                        :class="{on: flat[active] === item}" :aria-selected="String(flat[active] === item)"
                        @mouseenter="active = flat.indexOf(item)" @click="choose(item)">
                    <span class="gs-title">{{ item.title }}</span>
                    <span class="gs-sub">{{ item.subtitle }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import ApiService from "../utils/ApiService";
import {visibleNav} from "./navigation";

// Where a result opens, and which menu entry the user must have to go there.
const TARGETS = {
    units: (item, q) => ({path: '/buildium/rental-units', query: {q: item.unit_q || q}}),
    tenants: (item, q) => ({path: '/buildium/rental-units', query: {q: item.unit_q || q}}),
    properties: (item) => ({path: '/buildium/rental-properties', query: {name: item.title}}),
    owners: (item) => ({path: '/buildium/rental-owners', query: {q: item.title}}),
    association_owners: (item) => ({path: '/buildium/association-owners', query: {q: item.title}}),
    forms: (item) => (item.form_type === 'inspection-report'
        ? {path: '/forms', query: {type: 'inspection-report', open: String(item.id)}}
        : {path: '/forms', query: {type: item.form_type, open: String(item.id)}}),
};

export default {
    name: "GlobalSearch",
    data() {
        return {q: '', open: false, loading: false, groups: [], active: 0, timer: null, seq: 0};
    },
    computed: {
        isMac() {
            return /Mac|iPhone|iPad/.test(navigator.platform || '');
        },
        placeholder() {
            return 'Search units, tenants, notices…';
        },
        reachable() {
            const paths = new Set();
            visibleNav(this.$store.getters.userInfo || {}).forEach(g => g.items.forEach(i => paths.add(i.to.split('?')[0])));
            return paths;
        },
        flat() {
            return this.groups.reduce((all, g) => all.concat(g.items), []);
        },
    },
    watch: {
        q(value) {
            clearTimeout(this.timer);
            if (value.trim().length < 2) {
                this.groups = [];
                return;
            }
            this.timer = setTimeout(this.search, 250);
        },
    },
    methods: {
        search() {
            const seq = ++this.seq;
            const q = this.q.trim();
            this.loading = true;
            ApiService.get('/sterling/search', {params: {q}}).then(r => {
                if (seq !== this.seq) return;
                this.groups = (r.data.groups || []).map(g => ({
                    ...g,
                    items: g.items.map(item => this.decorate(g.kind, item, q)).filter(Boolean),
                })).filter(g => g.items.length);
                this.active = 0;
            }).catch(() => {
                if (seq === this.seq) this.groups = [];
            }).finally(() => {
                if (seq === this.seq) this.loading = false;
            });
        },
        decorate(kind, item, q) {
            const make = TARGETS[kind];
            if (!make) return null;
            const to = make(item, q);
            const navPath = kind === 'forms' ? '/forms' : (kind === 'association_owners' ? '/buildium/rental-owners' : to.path);
            if (!this.reachable.has(navPath) && !(kind === 'forms' && this.reachable.has('/inspection-reports'))) {
                return null;
            }
            return {...item, to};
        },
        move(step) {
            if (!this.flat.length) return;
            this.active = (this.active + step + this.flat.length) % this.flat.length;
        },
        choose(item) {
            if (!item) return;
            this.close();
            this.$router.push(item.to).catch(() => {
            });
        },
        close() {
            this.open = false;
            this.$refs.input && this.$refs.input.blur();
        },
        onBlur() {
            setTimeout(() => {
                this.open = false;
            }, 120);
        },
        onKey(e) {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                this.$refs.input && this.$refs.input.focus();
            }
        },
    },
    mounted() {
        window.addEventListener('keydown', this.onKey);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onKey);
    },
}
</script>

<style scoped>
.gs {
    position: relative;
    flex: 1;
    max-width: 560px;
    margin: 0 24px;
}

.gs-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    margin: 0;
    padding: 0 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, .12);
    border: 1px solid rgba(255, 255, 255, .18);
    color: #C9D3EA;
    cursor: text;
}

.gs-box.focus {
    background: #FFFFFF;
    color: #5B6478;
}

.gs-box input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: inherit;
    font-size: 14px;
}

.gs-box.focus input {
    color: #1C2433;
}

.gs-box input::placeholder {
    color: #C9D3EA;
}

.gs-box.focus input::placeholder {
    color: #8C97B3;
}

kbd {
    font-family: inherit;
    font-size: 11px;
    padding: 1px 6px;
    border: 1px solid rgba(255, 255, 255, .35);
    border-radius: 5px;
    background: transparent;
    color: inherit;
}

.gs-panel {
    position: absolute;
    top: 42px;
    left: 0;
    right: 0;
    max-height: 70vh;
    overflow-y: auto;
    background: #FFFFFF;
    border: 1px solid #E3E6EE;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(20, 32, 63, .18);
    padding: 6px 0;
    z-index: 200;
    color: #1C2433;
}

.gs-label {
    padding: 8px 14px 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: #8C97B3;
}

.gs-item {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 10px;
    width: 100%;
    padding: 8px 14px;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
}

.gs-item.on {
    background: #EEF2FB;
}

.gs-title {
    font-weight: 600;
}

.gs-sub {
    color: #5B6478;
    font-size: 12px;
}

.gs-hint {
    margin-left: auto;
    font-size: 12px;
    color: #1F2D5D;
}

.gs-empty {
    padding: 12px 14px;
    color: #5B6478;
    font-size: 14px;
}

@media (max-width: 760px) {
    .gs {
        margin: 0 10px;
    }

    kbd {
        display: none;
    }
}
</style>
